/**
 * Configuração do tenant (white-label).
 *
 * Suporta dois modos:
 *
 * Modo 1 — Build-time (VITE_*):
 *   Cada deploy define suas próprias variáveis de ambiente. Um deploy por cliente.
 *   VITE_TENANT_ID, VITE_APP_NAME, VITE_LOGO_URL, VITE_COLOR_THEME,
 *   VITE_DEFAULT_THEME, VITE_FEATURES, VITE_CSS_URL
 *
 * Modo 2 — Runtime por domínio (whitelabel dinâmico):
 *   Um único deploy serve múltiplos domínios. O frontend detecta o hostname
 *   e busca as configs no banco via GET /public/whitelabel?domain=xxx.
 *   Novos clientes são cadastrados no banco sem novo deploy.
 *   Ativado automaticamente quando VITE_TENANT_ID não está definido
 *   e o hostname não é localhost.
 */

export interface TenantConfig {
  id: string
  appName: string
  logoUrl: string | null
  colorTheme: string
  defaultTheme: 'light' | 'dark'
  features: string[]
  cssUrl: string | null
}

/** Config mutável — preenchida com VITE_* inicialmente; pode ser sobrescrita
 *  por resolveWhitelabelConfig() antes do React montar. */
export const tenantConfig: TenantConfig = {
  id: import.meta.env.VITE_TENANT_ID || 'default',
  appName: import.meta.env.VITE_APP_NAME || 'Rheoly',
  logoUrl: import.meta.env.VITE_LOGO_URL || null,
  colorTheme: import.meta.env.VITE_COLOR_THEME || 'blue',
  defaultTheme: (['light', 'dark'].includes(import.meta.env.VITE_DEFAULT_THEME)
    ? import.meta.env.VITE_DEFAULT_THEME
    : 'light') as 'light' | 'dark',
  features: import.meta.env.VITE_FEATURES
    ? import.meta.env.VITE_FEATURES.split(',').map((f: string) => f.trim()).filter(Boolean)
    : [],
  cssUrl: import.meta.env.VITE_CSS_URL || null,
}

/**
 * Tenta buscar a config de branding do domínio atual via API (Modo 2).
 * Se encontrar, sobrescreve tenantConfig em-place antes do React montar.
 *
 * Não faz nada em localhost ou quando VITE_TENANT_ID já está configurado
 * explicitamente (Modo 1 — deploy dedicado).
 */
export async function resolveWhitelabelConfig(): Promise<void> {
  // Modo 1: deploy dedicado com VITE_TENANT_ID explícito → não busca na API
  if (import.meta.env.VITE_TENANT_ID && import.meta.env.VITE_TENANT_ID !== 'default') return

  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') return

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    const res = await fetch(
      `${apiUrl}/public/whitelabel?domain=${encodeURIComponent(hostname)}`,
    )
    if (!res.ok) return

    const data = await res.json()
    if (!data) return

    Object.assign(tenantConfig, {
      id: data.tenantId ?? tenantConfig.id,
      appName: data.appName ?? tenantConfig.appName,
      logoUrl: data.logoUrl ?? tenantConfig.logoUrl,
      colorTheme: data.colorTheme ?? tenantConfig.colorTheme,
      defaultTheme: (['light', 'dark'].includes(data.defaultTheme)
        ? data.defaultTheme
        : tenantConfig.defaultTheme) as 'light' | 'dark',
      features: Array.isArray(data.features) ? data.features : tenantConfig.features,
      cssUrl: data.cssUrl ?? tenantConfig.cssUrl,
    })
  } catch {
    // silently fallback to VITE_* defaults
  }
}

/**
 * Injeta o CSS externo do tenant no <head> do documento.
 * Chamado em main.tsx após resolveWhitelabelConfig().
 */
export function injectTenantStyles(): void {
  if (!tenantConfig.cssUrl) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = tenantConfig.cssUrl
  link.setAttribute('data-tenant', tenantConfig.id)
  document.head.appendChild(link)
}

/**
 * Verifica se uma feature está habilitada para este tenant.
 * Se features estiver vazio, todas as features estão habilitadas.
 */
export function hasFeature(feature: string): boolean {
  if (tenantConfig.features.length === 0) return true
  return tenantConfig.features.includes(feature)
}
